import { Viewport } from "../map";
// This type is for use throughout the code where the "ID" could be a value from more than one type.
// This will also make it convenient if the agency IDs are every replaced by actual IDs instead of
// the current AgencyCode values.
export type AgencyId = AgencyCode | WspDistrictAgencyId;

export interface Agency {
  id: AgencyCode;
  jogId: JogId;
  category: AgencyCategory;
  name: string;
  shortName: string;
  isFilter: boolean;
  isUserAgency: boolean;
}

export interface Jog {
  id: JogId;    
  agencies: Agency[];
  name: string;
  shortName: string;  
  isFilter: boolean;
  isUserJog: boolean;
  isGroupOnly: boolean;
}

export enum AgencyCategory {
  //City = "City", // Probably not needed for Category filter.

  // Confirmed/requested by Aisha
  LawEnforcement = "Law Enforcement",
  Fire = "Fire",
  PublicTransit = "Transit",
  DOT = "Transportation",
  PublicWorks = "Public Works",
  PublicUtilities = "Public Utilities", // Added by Aisha  
  EmergencyMgmt = "Emergency Mgmt",
  Other = "Other",

  // Hack for filter UI DEMO.
  Jog = "JOG",
  Category = "Category",
}

export enum JogId {
  // Not actual JOG  
  LegacyAgencyGroup = "Legacy", // Supports existing list of user agencies, before switching to use "Agencies" array.
  Wsp = "WSPJog", // No events associated with this ID. Just used to group all Districts for dispatch event filter group.
  Other = "00",  // "No JOG"

  // Actual JOG or Group
  SeattleArea = "01", // "SEA"
  KingCountyNortheast = "02", // "NEJOG"
  KingCountySouth = "03", // "South JOG"
  Tacoma = "04", // "TAC"
  JBLM = "05",
  SnohomishCounty = "06",
  KitsapPeninsula = "07",
  ClarkCounty = "08",
  LowerColumbiaRiver = "11",
  NorthwestWashington = "17", // Includes SkagitCounty & WhatcomCounty.
  
  // Group, not actual JOG. No incidents or notifications. 
  EasternWashington = "18", // All area covered by WSP districts 3, 4, 6. Mainly added for IRT users in those areas.

  // Possible future JOGs
  LewisCounty = "10",
  Spokane = "13",
  TriCities = "15",
  
  // Obsolete - Leaving commented to note ID has been used.
  // CowlitzCounty = "09",  // Combined Lower Columbia River JOG
  // ThurstonCounty = "14", // Not currently in progress.
  // SkagitCounty = "12",   // Replaced by NW WA JOG. 
  // WhatcomCounty = "16",  // Replaced by NW WA JOG.
}

// JogIdsToRetrieveAllWspEvents is based on the SetJogIds(DispatchEvent data) function,
// in vcc-app-dispatch, where JogId is set for WSP dispatch events:
// https://dev.azure.com/WSDOT/VCC/_git/vcc-app-dispatch?path=/src/functions/DispatchEventPrepare/Function.cs&version=GBjog-main&line=160&lineEnd=161&lineStartColumn=1&lineEndColumn=1&lineStyle=plain&_a=contents
// This set of jogs will make sure we get events for all WSP districts, 1-8.
export const JogIdsToRetrieveAllWspEvents = [JogId.Tacoma, JogId.KingCountySouth, JogId.EasternWashington, JogId.ClarkCounty, JogId.NorthwestWashington, JogId.KitsapPeninsula];
export const AllJogIds = [
  JogId.SeattleArea,  
  JogId.KingCountyNortheast,
  JogId.KingCountySouth,
  JogId.Tacoma,
  JogId.JBLM,
  JogId.SnohomishCounty,
  JogId.KitsapPeninsula,
  JogId.ClarkCounty,
  JogId.LowerColumbiaRiver,
  JogId.NorthwestWashington,
  JogId.EasternWashington,
];

export enum AgencyCode {
  NOT_SELECTED = "",
  OTHER = "Other",

  // Statewide / Multiple JOG
  WSP = "WSP",
  WSDOT = "WSDOT",
  NWSA = "NWSA", // ?
  KCM = "KCM",

  // SAJOG #################################  
  SDOT = "SDOT",
  SFD = "SFD",
  SPD = "SPD",
  Port = "Port",

  // NEJOG #################################
  CityOfBellevue = "COB",
  BellevueFire = "BellFD",
  BellevueDot = "BellDOT",
  BellevuePolice = "BellPD",
  BothellEmergencyMgmt = "BothEM",
  BothellPolice = "BothPD",
  EastsideFire = "EFR",
  FallCityFire = "FallCityFD",
  IssaquahFire = "IssaFD",
  IssaquahPublicWorks = "IssaPW",
  KingCountyEmergencyMgmt = "KingCoEM",
  KirklandFire = "KirkFD",
  KirklandPublicWorks = "KirkPW",
  MedinaPolice = "MDPD",
  MedinaPublicWorks = "MDPW",
  MercerIslandPolice = "MIPD",
  CityOfNewcastle = "NewCastle",
  NewcastlePublicWorks = "NewCastlePW",
  Norcom = "NORCOM",
  RedmondFire = "RedFD",
  RedmondPolice = "RedPD",
  RedmondPublicWorks = "RedPW",
  ShorelineEmergencyMgmt = "ShoreEM",
  ShorelineFire = "ShoreFD",
  SnoqualmieFire = "SnoFD",
  ST = "ST",
  //SoundTransitKC Metro/Link LightRail
  WaEmergencyMgmtDiv = "WAEM",

  // South King County JOG (South JOG) ##########
  AuburnPolice = "AubPD",
  CityOfSeaTac = "CityOfSeatac",
  CityOfTukwila = "CityOfTukwila",
  CovingtonPolice = "CovPD",
  DesMoinesPublicWorks = "DesMoinesPW",
  DesMoinesPolice = "DesMoinesPD",
  FederalWayEmergencyMgmt = "FedWayEM",
  FederalWayPolice = "FedWayPD",
  FederalWayPublicWorks = "FedWayPW",
  KingCountyMedicOne = "KingCoMedOne",
  KentPolice = "KentPD",
  MapleValleyPolice = "MapValPD",
  MapleValleyPublicWorks = "MapValPW",
  MapleValleyEmergencyMgmt = "MapValEM",
  MountainViewFireAndRescue = "MtViewFireAndResue",
  PierceTransit = "PierceTransit",
  PugetSoundRegionalFireAuthority = "PugSoundFire",
  SeaTacEmergencyManagement = "SeaTacEM",
  SeaTacPublicWorks = "SeaTacPW",
  //Sound Transit
  //Sound Transit- KC Metro/Link Light Rail
  TukwilaEmergencyManagement = "TukEM",
  TukwilaPolice = "TukPD",
  TukwilaPublicWorks = "TukPW",
  ValleyRegionalFireAuthority = "ValRegFire",
  //WA Emergency Management Division

  // Tacoma JOG #################################
  BonneyLakeEmergencyManagementt = "BLEM",
  BonneyLakePublicWorks = "BLPW",
  BuckelyFire = "BuckleyFire",
  CentralPierceFireAndRescue = "CPFR",
  CityOfTacomaVenuesAndEvents = "TacVenEvents",
  EastPierceFireAndRescue = "EPFR",
  GigHarborFire = "GigFire",
  GigHarborPolice = "GigPD",
  LakewoodPolice = "LWPD",
  MiltonPublicWorks = "MiltonPW",
  //NWSA/ Port of Tacoma
  OrtingEmergencyManagement = "OrtEM",
  OrtingPolice = "OrtPD",
  PierceCountyEmergencyMgmt = "PierceEM",
  PierceCountyPlanningAndPublicWorks = "PiercePW",
  PierceCountySheriffsOffice = "PierceSheriff",
  //PierceTransit = "PierceTransit", // Tacoma and South KC JOG
  PuyallupEmergencyManagement = "PuyEM",
  PuyallupPolice = "PuyPD",
  PuyallupPublicWorks = "PuyPW",
  //Sound Transit
  SouthSound911 = "SouthSound911",
  SteilacoomPolice = "SteilPD",
  SumnerEmergencyManagement = "SumEM",
  SumnerPublicWorks = "SumPW",
  TacomaFireOperations = "TacFireOps",
  TacomaFireEmergencyMngmt = "TacFireEM",
  TacomaPublicWorks = "TacPW",
  TacomaRail = "TacRail",
  UniversityPlacePolice = "UPPD",
  //WA Emergency Management Division
  WestPierceFireAndRescue = "WPFR",
  WestPierceFireAndRescueEmergancyMgmt = "WPFREM",

  // Snohomish County JOG ######################
  CityOfGoldBar = "GLD",
  CityOfSultan = "SUL",
  CityOfLakeStevens = "LKSTV",
  CityOfMarysville = "MARY",

  ArlingtonPolice = "ArlPD",
  // BothellPolice = "BothPD", // Also in NEJOG
  BrierPublicWorks = "BrierPW",
  CommunityTransit = "CommTransit",
  EdmondsPoplice = "EdPD",
  EdmondsPublicWorks = "EdPW",
  EverettFire = "EvFD",
  EverettPolice = "EvPD",
  EverettPublicWorks = "EvPW",
  GoldBarPolice = "GBPD",
  GoldBarPublicWorks = "GBPW",
  LakeStevensPolice = "LSPD",
  LakeStevensPublicWorks = "LSPW",
  LynnwoodPublicWorks = "LynnPW",
  MarysvilleFire = "MaryFD",
  MarysvillePolice = "MARYPD",
  MarysvillePublicWorks = "MaryPW",
  MonroePolice = "MonPD",
  MountlakeTerracePublicWorks = "MLTPW",
  MukilteoPolice = "MukPD",
  NorthCountyRegionalFireAuthority = "NoCoFR",
  SnoCoEmergencyManagement = "SnoEM",
  SnohomishRegionalFireAndRescue = "SnoRegFR",
  SoundTransit = "ST",
  SouthCountyFire = "SoCoFire",
  StanwoodPublicWorks = "StanPW",
  StillaguamishPoplice = "StillPD",
  SultanPublicWorks = "SulPW",
  TulalipTribalPolice = "TulPD",
  //WA Emergency Management Division

  // Kitsap Peninsula JOG #######################
  BainbridgeIslandFire = "BIFD",
  BainbridgeIslandEmergencyManagement = "BIEM",
  BainbridgeIslandPolice = "BIPD",
  BremertonFire = "BIFD",
  BremertonPolice = "BremPD",
  BremertonPublicWorks = "BremPW",
  BrinnonFire = "BrinFD",
  CentralKitsapFireAndRescue = "CenKitFR",
  ClallamTransit = "ClAmTransit",
  EastJeffersonFireAndRescue = "EJFR",
  JeffersonCountyDeptOfEmergencyManagement = "JCEM",
  JeffersonCountySheriffsOffice = "JCSO",
  JeffersonTransitAuthority = "JTA",
  KitsapCountyDeptOfEmergencyManagement = "KitCoEM",
  KitsapCountySheriffsOffice = "KitCoSO",
  KitsapTransit = "KitTran",
  MACECOM911 = "MACECOM911",
  MasonCountyDeptOfEmergencyManagement = "MasCoEM",
  MasonCountyPublicWorks = "MasCoPW",
  MasonTransitAuthority = "MasTA",
  NavyRegionNorthwestFire = "NavyRegNWF",
  NavyShipyardEmergencyManagement = "NavySYEM",
  NorthKitsapFireAndRescue = "NKitFR",
  PortAngelesFire = "PAFD",
  PortGambleSKlallamTribeEmergencyManagement = "PGSTEM",
  PortOrchardPolice = "POPD",
  PortOrchardPublicWorks = "POPW",
  PortTownsendPolice = "PTPD",
  PoulsboEmergencyManagement = "PoulsEM",
  PoulsboFire = "PoulsFD",
  PoulsboPublicWorks = "PoulsPW",
  QuilceneFire = "QuilceneFD",
  SequimPolice = "SeqPD",
  SequimPublicWorks = "SeqPW",
  SheltonPublicWorks = "ShelPW",
  SkokomishTribalPolice = "SkoPD",
  SouthKitsapFireAndRescue = "SKitFR",

  // etc, etc
}

// Values that are saved in the dispatch event areaCode/ac field by dispatch processing
export enum AreaCode {
  I5 = "I5",
  WSP_D1 = "D1",
  WSP_D2 = "D2",
  WSP_D3 = "D3",
  WSP_D4 = "D4",
  WSP_D5 = "D5",
  WSP_D6 = "D6",
  WSP_D7 = "D7",
  WSP_D8 = "D8",
}

// Values that are saved in the dispatch event regionCode/rc field by dispatch processing
export enum RegionCode {
  NW = "NW",    // Northwest
  SEA = "SEA",  // King County,
  TAC = "TAC",  // Thurston, Peirce counties,
  SW = "SW",    // Southwest
  NC = "NC",    // North Central
  S = "S",      // South Central and Southeast
  E = "E",      // East
  PEN = "PEN",  // Olympic Peninsula
}

// Labels that can be edited
export enum WspDistrictLabel {
  D1_TAC = "TAC", // Tacoma
  D2_SEA = "SEA", // Seattle
  D3_YAK = "YAK", // Yakima
  D4_SPO = "SPO", // Spokane
  D5_VAN = "VAN", // Vancouver
  D6_WEN = "WEN", // Wenatchee
  D7_NW = "NW",   // Northwest
  D8_PEN = "PEN", // Kitsap Peninsula
}

export const AgencyDelimeter = ": ";

// IDs that should not be edited, because they're saved in UserPreferences
export class WspDistrictAgencyId  {
  static TAC_D1 = AgencyCode.WSP + AgencyDelimeter + AreaCode.WSP_D1
  static SEA_D2 = AgencyCode.WSP + AgencyDelimeter + AreaCode.WSP_D2
  static YAK_D3 = AgencyCode.WSP + AgencyDelimeter + AreaCode.WSP_D3
  static SPO_D4 = AgencyCode.WSP + AgencyDelimeter + AreaCode.WSP_D4
  static VAN_D5 = AgencyCode.WSP + AgencyDelimeter + AreaCode.WSP_D5
  static WEN_D6 = AgencyCode.WSP + AgencyDelimeter + AreaCode.WSP_D6
  static NW_D7 = AgencyCode.WSP + AgencyDelimeter + AreaCode.WSP_D7
  static PEN_D8 = AgencyCode.WSP + AgencyDelimeter + AreaCode.WSP_D8
}

// Labels that can be edited
export class WspAgencyLabel {
  // TODO: Consider whether we still need these as AgencyCodes or not.
  // May just need the AgencyRegionCode values. UI could use the combo of both enums, merhaps.
  // E.g., AgencyCode.WSP + ":" + AgencyRegionCode.NW
  static WSP = AgencyCode.WSP;
  static TAC_D1 = AgencyCode.WSP + AgencyDelimeter + WspDistrictLabel.D1_TAC;  // District 1 (Pierce and Thurston county)
  static SEA_D2 = AgencyCode.WSP + AgencyDelimeter + WspDistrictLabel.D2_SEA;  // District 2 (King County)
  static YAK_D3 = AgencyCode.WSP + AgencyDelimeter + WspDistrictLabel.D3_YAK;  // District 3
  static SPO_D4 = AgencyCode.WSP + AgencyDelimeter + WspDistrictLabel.D4_SPO;  // District 4
  static VAN_D5 = AgencyCode.WSP + AgencyDelimeter + WspDistrictLabel.D5_VAN;  // District 5
  static WEN_D6 = AgencyCode.WSP + AgencyDelimeter + WspDistrictLabel.D6_WEN;  // District 6
  static NW_D7 = AgencyCode.WSP + AgencyDelimeter + WspDistrictLabel.D7_NW;    // District 7
  static PEN_D8 = AgencyCode.WSP + AgencyDelimeter + WspDistrictLabel.D8_PEN;  // District 8

  // Unused
  static I5 = AgencyCode.WSP + AgencyDelimeter + AreaCode.I5; // Subset of District 2

  // Obsolete
  static S_D3 = AgencyCode.WSP + AgencyDelimeter + RegionCode.S;   // District 3 // todo: rename to S_D3_obsolete
  static E_D4 = AgencyCode.WSP + AgencyDelimeter + RegionCode.E;   // District 4 // todo: rename to E_D4_obsolete
  static SW_D5 = AgencyCode.WSP + AgencyDelimeter + RegionCode.SW; // District 5 // todo: rename to SW_D5_obsolete
  static NC_D6 = AgencyCode.WSP + AgencyDelimeter + RegionCode.NC; // District 6 // todo: rename to NC_D6_obsolete

  static WSP_North = "WSP-North"; // TODO: Re-work? This is a hard coded menu item value when inviting or editing a user. Needed still, or replaced by "WSP: NC" / "WSP: N"?
}

// Purposely exporting this map for readability. Where it's used, you can
// tell if there's not a converted value the existing value should be used.
export const AgencyFilterConversionMap = new Map<AgencyId, string>([  
  // Obsolete WSP filters
  [WspAgencyLabel.TAC_D1, WspDistrictAgencyId.TAC_D1],
  [WspAgencyLabel.SEA_D2, WspDistrictAgencyId.SEA_D2],
  [WspAgencyLabel.S_D3, WspDistrictAgencyId.YAK_D3],  
  [WspAgencyLabel.E_D4, WspDistrictAgencyId.SPO_D4],
  [WspAgencyLabel.SW_D5, WspDistrictAgencyId.VAN_D5],
  [WspAgencyLabel.NC_D6, WspDistrictAgencyId.WEN_D6],
  [WspAgencyLabel.NW_D7, WspDistrictAgencyId.NW_D7],
  [WspAgencyLabel.PEN_D8, WspDistrictAgencyId.PEN_D8],  
]);

export const JogIdConversionMap = new Map<string, JogId>([  
  // Obsolete WSP filters
  ["0", JogId.Other],
  ["1", JogId.SeattleArea],
  ["2", JogId.KingCountyNortheast],
  ["3", JogId.KingCountySouth],
  ["4", JogId.Tacoma],
  ["5", JogId.JBLM],
  ["6", JogId.SnohomishCounty],
  ["7", JogId.KitsapPeninsula],
  ["8", JogId.ClarkCounty],
]);

// Purposely not exporting the map
const AgencyFilterLabels = new Map<AgencyId, string>([
  [WspDistrictAgencyId.TAC_D1, WspAgencyLabel.TAC_D1],
  [WspDistrictAgencyId.SEA_D2, WspAgencyLabel.SEA_D2],
  [WspDistrictAgencyId.YAK_D3, WspAgencyLabel.YAK_D3],
  [WspDistrictAgencyId.SPO_D4, WspAgencyLabel.SPO_D4],
  [WspDistrictAgencyId.VAN_D5, WspAgencyLabel.VAN_D5],
  [WspDistrictAgencyId.WEN_D6, WspAgencyLabel.WEN_D6],
  [WspDistrictAgencyId.NW_D7, WspAgencyLabel.NW_D7],
  [WspDistrictAgencyId.PEN_D8, WspAgencyLabel.PEN_D8],
]);

// Using this function instead of direct map access to uniformly return the agencyId if there is no label.
// This works currently because all of the filters that aren't in the map use their AreaCode as the ID.
export const GetAgencyFilterLabel = (agencyId: AgencyId): string => {
  return AgencyFilterLabels.get(agencyId) || agencyId as string;
}

const makeAgency = (jogId: JogId, id: AgencyId, name: string, shortName: string, category: AgencyCategory, isFilter = false, isUserAgency = true): Agency => {
  return {
    shortName,
    category,
    id,
    name,
    jogId,
    isUserAgency,
    isFilter
  } as Agency;
}

const notIsUserAgency = false;
const isUserJog = true;
const notIsUserJog = !isUserJog;
const isGroupOnly = true;
const isFilter = true;
const notIsFilter = !isFilter;

const Agencies: Agency[] = [];

// Agencies that exist in multiple JOGs are defined here for easy maintainance. And readability; when you see these added to the Agencies array, you know they are in multiple JOGs.
// When an agency is in multiple JOGs, they need to be added to the array multiple times, once for each JogId, so that the JOG filteres and user invite and edit UIs work correctly.
const kingCountyMetro = makeAgency(JogId.SeattleArea, AgencyCode.KCM, "King County Metro", "King Co. Metro", AgencyCategory.PublicTransit, isFilter);
const soundTransitAgency = makeAgency(JogId.SeattleArea, AgencyCode.SoundTransit, "Sound Transit", "Sound Transit", AgencyCategory.PublicTransit);
const wsdotAgency = makeAgency(JogId.Other, AgencyCode.WSDOT, "Washington State Dept. of Transportation", AgencyCode.WSDOT, AgencyCategory.DOT);
const wspAgency = makeAgency(JogId.Other, AgencyCode.WSP, "Washington State Patrol", AgencyCode.WSP, AgencyCategory.LawEnforcement);

// Other/Statewide
Agencies.push(...[
  makeAgency(JogId.Other, AgencyCode.NWSA, AgencyCode.NWSA, AgencyCode.NWSA, AgencyCategory.Other, notIsFilter),
  { ...wspAgency, jogId: JogId.Other, isUserAgency: true },
  { ...wsdotAgency, jogId: JogId.Other, isUserAgency: true },
]);

// Clark County
Agencies.push(...[
  makeAgency(JogId.ClarkCounty, WspDistrictAgencyId.VAN_D5, "Washington State Patrol (District 5)", WspAgencyLabel.SW_D5, AgencyCategory.LawEnforcement, isFilter, notIsUserAgency),
  { ...wsdotAgency, jogId: JogId.ClarkCounty },
  { ...wspAgency, jogId: JogId.ClarkCounty },
]);

// Cowlitz County
// Agencies.push(...[
//   makeAgency(JogId.CowlitzCounty, WspDistrictAgencyId.VAN_D5, "Washington State Patrol (District 5)", WspAgencyLabel.SW_D5, AgencyCategory.LawEnforcement, isFilter, notIsUserAgency),
//   { ...wsdotAgency, jogId: JogId.CowlitzCounty },
//   { ...wspAgency, jogId: JogId.CowlitzCounty },
// ]);

// Eastern WA Group
// todo: jcavner: Create reusable variables for these, like wspAgency?
Agencies.push(...[
  makeAgency(JogId.EasternWashington, WspDistrictAgencyId.YAK_D3, "Washington State Patrol (District 3)", WspAgencyLabel.YAK_D3, AgencyCategory.LawEnforcement, isFilter, notIsUserAgency),
  makeAgency(JogId.EasternWashington, WspDistrictAgencyId.SPO_D4, "Washington State Patrol (District 4)", WspAgencyLabel.SPO_D4, AgencyCategory.LawEnforcement, isFilter, notIsUserAgency),  
  makeAgency(JogId.EasternWashington, WspDistrictAgencyId.WEN_D6, "Washington State Patrol (District 6)", WspAgencyLabel.WEN_D6, AgencyCategory.LawEnforcement, isFilter, notIsUserAgency),
  { ...wsdotAgency, jogId: JogId.EasternWashington },
  { ...wspAgency, jogId: JogId.EasternWashington },
]);

// JBLM
Agencies.push(...[
  makeAgency(JogId.JBLM, WspDistrictAgencyId.TAC_D1, "Washington State Patrol (District 1)", WspAgencyLabel.TAC_D1, AgencyCategory.LawEnforcement, isFilter, notIsUserAgency),
  { ...wsdotAgency, jogId: JogId.JBLM },
  { ...wspAgency, jogId: JogId.JBLM },
]);

// Kitsap Peninsula
Agencies.push(...[
  makeAgency(JogId.KitsapPeninsula, WspDistrictAgencyId.PEN_D8, "Washington State Patrol (District 8)", WspAgencyLabel.PEN_D8, AgencyCategory.LawEnforcement, isFilter, notIsUserAgency),
  { ...wsdotAgency, jogId: JogId.KitsapPeninsula },
  { ...wspAgency, jogId: JogId.KitsapPeninsula },
]);

// Lewis County
// Agencies.push(...[
//   makeAgency(JogId.LewisCounty, WspDistrictAgencyId.VAN_D5, "Washington State Patrol (District 5)", WspAgencyLabel.SW_D5, AgencyCategory.LawEnforcement, isFilter, notIsUserAgency),
//   { ...wsdotAgency, jogId: JogId.LewisCounty },
//   { ...wspAgency, jogId: JogId.LewisCounty },
// ]);

// Lower Columbia River
Agencies.push(...[
  makeAgency(JogId.LowerColumbiaRiver, WspDistrictAgencyId.VAN_D5, "Washington State Patrol (District 5)", WspAgencyLabel.SW_D5, AgencyCategory.LawEnforcement, isFilter, notIsUserAgency),
  makeAgency(JogId.LowerColumbiaRiver, WspDistrictAgencyId.PEN_D8, "Washington State Patrol (District 8)", WspAgencyLabel.PEN_D8, AgencyCategory.LawEnforcement, isFilter, notIsUserAgency),
  { ...wsdotAgency, jogId: JogId.LowerColumbiaRiver },
  { ...wspAgency, jogId: JogId.LowerColumbiaRiver },
]);

// Northwest Washington
Agencies.push(...[
  makeAgency(JogId.NorthwestWashington, WspDistrictAgencyId.NW_D7, "Washington State Patrol (District 7)", WspAgencyLabel.NW_D7, AgencyCategory.LawEnforcement, isFilter, notIsUserAgency),
  { ...wsdotAgency, jogId: JogId.NorthwestWashington },
  { ...wspAgency, jogId: JogId.NorthwestWashington },
]);

// Seattle (SAJOG)
Agencies.push(...[
  { ...kingCountyMetro, jogId: JogId.SeattleArea },  
  makeAgency(JogId.SeattleArea, AgencyCode.Port, "Port of Seattle", "Port of Sea", AgencyCategory.Other),
  makeAgency(JogId.SeattleArea, AgencyCode.SDOT, "Seattle Dept. of Transportation", "Seattle DOT", AgencyCategory.DOT),
  makeAgency(JogId.SeattleArea, AgencyCode.SFD, "Seattle Fire Dept.", "Seattle FD", AgencyCategory.Fire, isFilter),
  makeAgency(JogId.SeattleArea, AgencyCode.SPD, "Seattle Police Dept.", "Seattle PD", AgencyCategory.LawEnforcement, isFilter),
  makeAgency(JogId.SeattleArea, WspDistrictAgencyId.SEA_D2, "Washington State Patrol (District 2)", WspAgencyLabel.SEA_D2, AgencyCategory.LawEnforcement, isFilter, notIsUserAgency),
  { ...soundTransitAgency, jogId: JogId.SeattleArea },
  { ...wsdotAgency, jogId: JogId.SeattleArea },
  { ...wspAgency, jogId: JogId.SeattleArea },
]);

// NE King County JOG (NEJOG)
Agencies.push(...[
  { ...kingCountyMetro, jogId: JogId.KingCountyNortheast },
  { ...soundTransitAgency, jogId: JogId.KingCountyNortheast },
  { ...wsdotAgency, jogId: JogId.KingCountyNortheast },
  { ...wspAgency, jogId: JogId.KingCountyNortheast },  

  // Bellevue
  makeAgency(JogId.KingCountyNortheast, AgencyCode.BellevueFire, "Bellevue Fire Dept.", "Bellevue FD", AgencyCategory.Fire),
  makeAgency(JogId.KingCountyNortheast, AgencyCode.BellevueDot, "Bellevue Dept. of Transportation", "Bellevue DOT", AgencyCategory.DOT),
  makeAgency(JogId.KingCountyNortheast, AgencyCode.BellevuePolice, "Bellevue Police Dept.", "Bellevue PD", AgencyCategory.LawEnforcement),
  makeAgency(JogId.KingCountyNortheast, AgencyCode.CityOfBellevue, "City of Bellevue", "Bellevue", AgencyCategory.Other),

  // Bothell
  makeAgency(JogId.KingCountyNortheast, AgencyCode.BothellEmergencyMgmt, "Bothell Emergency Management", "Bothell EM", AgencyCategory.EmergencyMgmt),
  makeAgency(JogId.KingCountyNortheast, AgencyCode.BothellPolice, "Bothell Police Dept.", "Bothell PD", AgencyCategory.LawEnforcement),

  makeAgency(JogId.KingCountyNortheast, AgencyCode.EastsideFire, "Eastside Fire", "Eastside Fire", AgencyCategory.Fire),

  makeAgency(JogId.KingCountyNortheast, AgencyCode.FallCityFire, "Fall City Fire", "Fall City FD", AgencyCategory.Fire),

  makeAgency(JogId.KingCountyNortheast, AgencyCode.IssaquahFire, "Issaquah Fire Dept.", "Issa. FD", AgencyCategory.Fire),

  makeAgency(JogId.KingCountyNortheast, AgencyCode.KingCountyEmergencyMgmt, "King County Emergency Management", "KingCo EM", AgencyCategory.EmergencyMgmt),

  // Kirkland
  makeAgency(JogId.KingCountyNortheast, AgencyCode.KirklandFire, "Kirkland Fire Dept", "Kirk. FD", AgencyCategory.Fire),
  makeAgency(JogId.KingCountyNortheast, AgencyCode.KirklandPublicWorks, "Kirkland Public Works", "Kirk. PW", AgencyCategory.PublicWorks),

  // Medina
  makeAgency(JogId.KingCountyNortheast, AgencyCode.MedinaPolice, "Medina Police Dept.", "Medina PD", AgencyCategory.LawEnforcement),
  makeAgency(JogId.KingCountyNortheast, AgencyCode.MedinaPublicWorks, "Medina Public Works", "Medina PW", AgencyCategory.PublicWorks),

  // Mercer Island
  makeAgency(JogId.KingCountyNortheast, AgencyCode.MercerIslandPolice, "Mercer Island Police Dept.", "MI PD", AgencyCategory.LawEnforcement),

  // Newcastle
  makeAgency(JogId.KingCountyNortheast, AgencyCode.CityOfNewcastle, "City of Newcastle", "Newcastle", AgencyCategory.Other),
  makeAgency(JogId.KingCountyNortheast, AgencyCode.NewcastlePublicWorks, "Newcastle Public Works", "Newcastle PW", AgencyCategory.PublicWorks),

  // NORCOM
  makeAgency(JogId.KingCountyNortheast, AgencyCode.Norcom, "NORCOM", "NORCOM", AgencyCategory.EmergencyMgmt),

  // Redmond
  makeAgency(JogId.KingCountyNortheast, AgencyCode.RedmondFire, "Redmond Fire Dept", "Redmond FD", AgencyCategory.Fire),
  makeAgency(JogId.KingCountyNortheast, AgencyCode.RedmondPolice, "Redmond Police Dept", "Redmond PD", AgencyCategory.LawEnforcement),
  makeAgency(JogId.KingCountyNortheast, AgencyCode.RedmondPublicWorks, "Redmond Public Works", "Redmond PW", AgencyCategory.PublicWorks),

  // Shoreline
  makeAgency(JogId.KingCountyNortheast, AgencyCode.ShorelineEmergencyMgmt, "Shoreline Emergency Management", "Shoreline EM", AgencyCategory.EmergencyMgmt),
  makeAgency(JogId.KingCountyNortheast, AgencyCode.ShorelineFire, "Shoreline Fire Dept", "Shoreline FD", AgencyCategory.Fire),

  // Snoqualmie
  makeAgency(JogId.KingCountyNortheast, AgencyCode.SnoqualmieFire, "Snoqualmie Fire Dept", "Snoqualmie FD", AgencyCategory.Fire),

  makeAgency(JogId.KingCountyNortheast, WspDistrictAgencyId.SEA_D2, "Washington State Patrol (District 2)", WspAgencyLabel.SEA_D2, AgencyCategory.LawEnforcement, isFilter, notIsUserAgency),
]);

// South King County JOG (South JOG)
Agencies.push(...[
  makeAgency(JogId.KingCountySouth, AgencyCode.AuburnPolice, "Auburn Police Dept.", "Aburn PD", AgencyCategory.LawEnforcement),
  makeAgency(JogId.KingCountySouth, AgencyCode.CityOfSeaTac, "City of SeaTac", "SeaTac", AgencyCategory.Other),
  makeAgency(JogId.KingCountySouth, AgencyCode.CityOfTukwila, "City of Tukwila", "Tukwila", AgencyCategory.Other),
  makeAgency(JogId.KingCountySouth, AgencyCode.CovingtonPolice, "Covington Police Dept.", "Covington PD", AgencyCategory.LawEnforcement),
  makeAgency(JogId.KingCountySouth, AgencyCode.DesMoinesPublicWorks, "Des Moines Public Works", "Des Moines PW", AgencyCategory.PublicWorks),
  makeAgency(JogId.KingCountySouth, AgencyCode.DesMoinesPolice, "Des Moines Police Dept", "Des Moines PD", AgencyCategory.LawEnforcement),
  makeAgency(JogId.KingCountySouth, AgencyCode.FederalWayEmergencyMgmt, "Federal Way Emergency Mgmt.", "Federal Way EM", AgencyCategory.EmergencyMgmt),
  makeAgency(JogId.KingCountySouth, AgencyCode.FederalWayPolice, "Federal Way Police Dept.", "Federal Way PD", AgencyCategory.LawEnforcement),
  makeAgency(JogId.KingCountySouth, AgencyCode.FederalWayPublicWorks, "Federal Way Public Works", "Federal Way PW", AgencyCategory.PublicWorks),
  makeAgency(JogId.KingCountySouth, AgencyCode.KentPolice, "Kent Police Dept.", "Kent PD", AgencyCategory.LawEnforcement),
  makeAgency(JogId.KingCountySouth, AgencyCode.KingCountyMedicOne, "King County Medic One", "KingCo MedOne", AgencyCategory.Other),
  makeAgency(JogId.KingCountySouth, AgencyCode.KingCountyMedicOne, "King County Emergency Mgmt.", "KingCo EM", AgencyCategory.EmergencyMgmt),
  { ...kingCountyMetro, jogId: JogId.KingCountySouth },
  makeAgency(JogId.KingCountySouth, AgencyCode.MapleValleyPolice, "Maple Valley Police Dept.", "Maple Valley PD", AgencyCategory.LawEnforcement),
  makeAgency(JogId.KingCountySouth, AgencyCode.MapleValleyPublicWorks, "Maple Valley Public Works", "Maple Valley PW", AgencyCategory.PublicWorks),
  makeAgency(JogId.KingCountySouth, AgencyCode.MapleValleyEmergencyMgmt, "Maple Valley Emergency Mgmt.", "Maple Valley EM", AgencyCategory.EmergencyMgmt),
  makeAgency(JogId.KingCountySouth, AgencyCode.MountainViewFireAndRescue, "Mountain View Fire and Rescue", "Mt. View FR", AgencyCategory.Other),
  makeAgency(JogId.KingCountySouth, AgencyCode.PierceTransit, "Pierce Transit", "Pierce Transit", AgencyCategory.PublicTransit), // Also in Tacoma JOG
  makeAgency(JogId.KingCountySouth, AgencyCode.PugetSoundRegionalFireAuthority, "Puget Sound Regional Fire Authority", "Puget Sound RFA", AgencyCategory.Fire),
  makeAgency(JogId.KingCountySouth, AgencyCode.SeaTacEmergencyManagement, "SeaTac Emergency Mgmt.", "SeaTac EM", AgencyCategory.EmergencyMgmt),
  makeAgency(JogId.KingCountySouth, AgencyCode.SeaTacEmergencyManagement, "SeaTac Public Works", "SeaTac PW", AgencyCategory.PublicWorks),
  makeAgency(JogId.KingCountySouth, AgencyCode.TukwilaEmergencyManagement, "Tukwila Emergency Management", "Tukwila EM", AgencyCategory.EmergencyMgmt),
  makeAgency(JogId.KingCountySouth, AgencyCode.TukwilaPolice, "Tukwila Police", "Tukwila PD", AgencyCategory.LawEnforcement),
  makeAgency(JogId.KingCountySouth, AgencyCode.TukwilaPublicWorks, "Tukwila Public Works", "Tukwila PW", AgencyCategory.PublicWorks),
  makeAgency(JogId.KingCountySouth, AgencyCode.ValleyRegionalFireAuthority, "Valley Regional Fire Authority", "Valley RFA", AgencyCategory.Fire),
  makeAgency(JogId.KingCountySouth, AgencyCode.WaEmergencyMgmtDiv, "Washington Emergency Management Division", "WA EM", AgencyCategory.EmergencyMgmt),
  makeAgency(JogId.KingCountySouth, WspDistrictAgencyId.SEA_D2, "Washington State Patrol (District 2)", WspAgencyLabel.SEA_D2, AgencyCategory.LawEnforcement, isFilter, notIsUserAgency),
  { ...soundTransitAgency, jogId: JogId.KingCountySouth },
  { ...wsdotAgency, jogId: JogId.KingCountySouth },
  { ...wspAgency, jogId: JogId.KingCountySouth },
]);

// Snohomish County JOG
Agencies.push(...[
  makeAgency(JogId.SnohomishCounty, AgencyCode.CityOfSultan, "City of Sultan", "Sultan", AgencyCategory.Other),
  makeAgency(JogId.SnohomishCounty, AgencyCode.CityOfGoldBar, "City of Gold Bar", "Gold Bar", AgencyCategory.Other),
  makeAgency(JogId.SnohomishCounty, AgencyCode.CityOfLakeStevens, "City of Lake Stevens", "Lake Stevens", AgencyCategory.Other),
  makeAgency(JogId.SnohomishCounty, AgencyCode.CityOfMarysville, "City of Marysville", "Marysville", AgencyCategory.Other),
  makeAgency(JogId.SnohomishCounty, AgencyCode.MarysvillePolice, "Marysville Police Dept.", "Marysville PD", AgencyCategory.LawEnforcement),
  makeAgency(JogId.SnohomishCounty, WspDistrictAgencyId.NW_D7, "Washington State Patrol (District 7)", WspAgencyLabel.NW_D7, AgencyCategory.LawEnforcement, isFilter, notIsUserAgency),
  { ...soundTransitAgency, jogId: JogId.SnohomishCounty },
  { ...wsdotAgency, jogId: JogId.SnohomishCounty },
  { ...wspAgency, jogId: JogId.SnohomishCounty },  
]);

// Spokane County
Agencies.push(...[
  makeAgency(JogId.Spokane, WspDistrictAgencyId.SPO_D4, "Washington State Patrol (District 4)", WspAgencyLabel.E_D4, AgencyCategory.LawEnforcement, isFilter, notIsUserAgency),
  { ...wsdotAgency, jogId: JogId.Spokane },
  { ...wspAgency, jogId: JogId.Spokane },  
]);

// Tacoma JOG
Agencies.push(...[
  makeAgency(JogId.Tacoma, WspDistrictAgencyId.TAC_D1, "Washington State Patrol (District 1)", WspAgencyLabel.TAC_D1, AgencyCategory.LawEnforcement, isFilter, notIsUserAgency),
  { ...soundTransitAgency, jogId: JogId.Tacoma },
  { ...wsdotAgency, jogId: JogId.Tacoma },
  { ...wspAgency, jogId: JogId.Tacoma },    
]);

// Thurston County JOG
// Agencies.push(...[
//   makeAgency(JogId.ThurstonCounty, WspDistrictAgencyId.WEN_D6, "Washington State Patrol (District 6)", WspAgencyLabel.WEN_D6, AgencyCategory.LawEnforcement, isFilter, notIsUserAgency),
//   {
//     ...wsdotAgency,
//     jogId: JogId.ThurstonCounty
//   },
//   { ...wspAgency, jogId: JogId.ThurstonCounty },
// ]);

// TriCities JOG
Agencies.push(...[
  makeAgency(JogId.TriCities, WspDistrictAgencyId.YAK_D3, "Washington State Patrol (District 3)", WspAgencyLabel.S_D3, AgencyCategory.LawEnforcement, isFilter, notIsUserAgency),
  { ...wsdotAgency, jogId: JogId.TriCities },
  { ...wspAgency, jogId: JogId.TriCities },
]);

// WSP Districts
Agencies.push(...[
  makeAgency(JogId.Wsp, WspDistrictAgencyId.TAC_D1, "Washington State Patrol (District 1)", WspAgencyLabel.TAC_D1, AgencyCategory.LawEnforcement, isFilter, notIsUserAgency),
  makeAgency(JogId.Wsp, WspDistrictAgencyId.SEA_D2, "Washington State Patrol (District 2)", WspAgencyLabel.SEA_D2, AgencyCategory.LawEnforcement, isFilter, notIsUserAgency),
  makeAgency(JogId.Wsp, WspDistrictAgencyId.YAK_D3, "Washington State Patrol (District 3)", WspAgencyLabel.YAK_D3, AgencyCategory.LawEnforcement, isFilter, notIsUserAgency),
  makeAgency(JogId.Wsp, WspDistrictAgencyId.SPO_D4, "Washington State Patrol (District 4)", WspAgencyLabel.SPO_D4, AgencyCategory.LawEnforcement, isFilter, notIsUserAgency),
  makeAgency(JogId.Wsp, WspDistrictAgencyId.VAN_D5, "Washington State Patrol (District 5)", WspAgencyLabel.VAN_D5, AgencyCategory.LawEnforcement, isFilter, notIsUserAgency),
  makeAgency(JogId.Wsp, WspDistrictAgencyId.WEN_D6, "Washington State Patrol (District 6)", WspAgencyLabel.WEN_D6, AgencyCategory.LawEnforcement, isFilter, notIsUserAgency),
  makeAgency(JogId.Wsp, WspDistrictAgencyId.NW_D7, "Washington State Patrol (District 7)", WspAgencyLabel.NW_D7, AgencyCategory.LawEnforcement, isFilter, notIsUserAgency),
  makeAgency(JogId.Wsp, WspDistrictAgencyId.PEN_D8, "Washington State Patrol (District 8)", WspAgencyLabel.PEN_D8, AgencyCategory.LawEnforcement, isFilter, notIsUserAgency),
]);

const makeJog = (id: JogId, name: string, shortName: string, isFilter = true, isUserJog = true, isGroupOnly = false): Jog => {
  return {
    id,
    name,
    shortName,
    agencies: getJogAgencies(id, Agencies),
    isFilter,
    isUserJog,
    isGroupOnly,
  }
}

const agencyJogsMap = new Map<AgencyId, Set<JogId>>();

// todo: jcavner: there's a potential for an issue here
export const getAgencyJogIds = (id: AgencyId): Set<JogId> | undefined => {
  return  agencyJogsMap.get(id);
}

const getJogAgencies = (jogId: JogId, agencies: Agency[]): Agency[] => {
  const jogAgencies: Agency[] = [];
  agencies.forEach((agency) => {
    if (agency.jogId === jogId) {
      jogAgencies.push(agency);

      let set = agencyJogsMap.get(agency.id);
      if(set) {
        set.add(agency.jogId);
      } else {
        agencyJogsMap.set(agency.id, new Set([agency.jogId]));
      }      
    }
  })

  return jogAgencies;
}

const currentJogs: Jog[] = [
  makeJog(JogId.Other, "Other/Unassigned", "Other", notIsFilter, notIsUserJog),
  
  // "Participating"
  makeJog(JogId.ClarkCounty, "Clark County JOG", "Clark Co."),
  makeJog(JogId.JBLM, "Joint Base Lewis-McChord JOG", "JBLM"),
  makeJog(JogId.KingCountyNortheast, "Northeast King County JOG", "NEJOG"),
  makeJog(JogId.KingCountySouth, "South King County JOG", "South JOG"),
  makeJog(JogId.KitsapPeninsula, "Kitsap Peninsula JOG", "Kitsap Pen."),
  makeJog(JogId.LowerColumbiaRiver, "Lower Columbia River JOG", "Lower Col. River"),
  makeJog(JogId.NorthwestWashington, "Northwest WA JOG", "NW WA"),
  makeJog(JogId.SeattleArea, "Seattle Area JOG", "SAJOG"),
  makeJog(JogId.SnohomishCounty, "Snohomish County JOG", "Snohomish Co."),
  makeJog(JogId.Tacoma, "Tacoma JOG", "Tacoma"),

  // "Group" (not an actual JOG)
  makeJog(JogId.EasternWashington, "Eastern WA Group", "Eastern WA", isFilter, isUserJog, isGroupOnly), // Temp group for areas covered by WSP districts 3, 4, 6. Not a JOG. // todo: jcavner: verify it's on notifications preferences page. Actually, we're not going to create incidents so it doesn't need to be there for launch
  makeJog(JogId.Wsp, "Washington State Patrol Districts", "WSP Districts", isFilter, notIsUserJog, isGroupOnly),
]

// eslint-disable-next-line
const futureJogs: Jog[] = [
  // Other, not finalized
  //makeJog(JogId.ThurstonCounty, "Thurston County JOG", "Thurston Co.", notIsFilter, notIsUserJog), // Not on notifications prefs dialog
  makeJog(JogId.TriCities, "Tri-Cities JOG", "Tri-Cities", notIsFilter, notIsUserJog), // Not on notifications prefs dialog
  //makeJog(JogId.CowlitzCounty, "Cowlitz County JOG", "Cowlitz Co.", notIsFilter, notIsUserJog), // Not on notifications prefs dialog
  makeJog(JogId.LewisCounty, "Lewis County JOG", "Lewis Co.", notIsFilter, notIsUserJog), // Not on notifications prefs dialog  
  makeJog(JogId.Spokane, "Spokane JOG", "Spokane", notIsFilter, notIsUserJog), // Not on notifications prefs dialog
]

export const Jogs: Jog[] = currentJogs;//.concat(futureJogs);

const setJogMap = (): Map<string, Jog> => {
  let jogMap = new Map<string, Jog>();
  Jogs.forEach((jog) => {
    jogMap.set(jog.id, jog)
  })

  return jogMap;
}

export const JogMap = setJogMap();
// todo: create GetJog function ?

const setAgencyMap = (): Map<AgencyId, Agency> => {
  let agencyMap = new Map<string, Agency>();
  Agencies.forEach((agency) => {
    agencyMap.set(agency.id, agency)
  })

  return agencyMap;
}

// NOTE: If an angecy is "created twice," so it can be put into more than one JOG,
// this map will only have one copy of it, with a reference to only one of the JOGs it's in.
// This could lead to bugs if the agency details are not the same between the copies. Or if 
// this map was used to find out what jog an agency is in.
const AgencyMap = setAgencyMap();

// Used in agency-chip
export const GetAgency = (agencyId: AgencyId) => {
  return AgencyMap.get(agencyId);
}

// Supports pre-JOG existing lists of agencies, before switching to use "Agencies" array.
export const LegacyAgencyGroup: Jog = {
  shortName: "LAG",
  id: JogId.LegacyAgencyGroup,
  name: "Legacy Agency Group",

  // these are in "display order"
  agencies: [
    kingCountyMetro,
    AgencyMap.get(AgencyCode.NWSA),
    AgencyMap.get(AgencyCode.Port),
    AgencyMap.get(AgencyCode.SDOT),
    AgencyMap.get(AgencyCode.SFD),
    AgencyMap.get(AgencyCode.SPD),
    { ...soundTransitAgency },
    { ...wsdotAgency },
    { ...wspAgency },
    AgencyMap.get(AgencyCode.CityOfBellevue),
    AgencyMap.get(AgencyCode.CityOfGoldBar),
    AgencyMap.get(AgencyCode.CityOfSultan),
    AgencyMap.get(AgencyCode.EastsideFire),
    AgencyMap.get(AgencyCode.MedinaPolice),
    AgencyMap.get(AgencyCode.MarysvillePolice),
    AgencyMap.get(AgencyCode.CityOfMarysville),
    AgencyMap.get(AgencyCode.CityOfLakeStevens),
  ]
} as Jog;

// Used in agency-chip
export const getWspLabel = (area: AreaCode | string = ""): WspAgencyLabel => {
  switch (area) {
    case AreaCode.WSP_D1: return WspAgencyLabel.TAC_D1;
    case AreaCode.WSP_D2:
    case AreaCode.I5: // The area value of D2 events within specific coordinates
      return WspAgencyLabel.SEA_D2;
    case AreaCode.WSP_D3: return WspAgencyLabel.YAK_D3;
    case AreaCode.WSP_D4: return WspAgencyLabel.SPO_D4;
    case AreaCode.WSP_D5: return WspAgencyLabel.VAN_D5;
    case AreaCode.WSP_D6: return WspAgencyLabel.WEN_D6;
    case AreaCode.WSP_D7: return WspAgencyLabel.NW_D7;
    case AreaCode.WSP_D8: return WspAgencyLabel.PEN_D8;
    default:
      return WspAgencyLabel.WSP;
  }
};

// Used in filter click handler
export const getWspDistrictAgencyId = (area: AreaCode | string = ""): WspDistrictAgencyId | undefined => {
  switch (area) {
    case AreaCode.WSP_D1: return WspDistrictAgencyId.TAC_D1;
    case AreaCode.WSP_D2:
    case AreaCode.I5: // The area value of D2 events within specific coordinates
      return WspDistrictAgencyId.SEA_D2;
    case AreaCode.WSP_D3: return WspDistrictAgencyId.YAK_D3;
    case AreaCode.WSP_D4: return WspDistrictAgencyId.SPO_D4;
    case AreaCode.WSP_D5: return WspDistrictAgencyId.VAN_D5;
    case AreaCode.WSP_D6: return WspDistrictAgencyId.WEN_D6;
    case AreaCode.WSP_D7: return WspDistrictAgencyId.NW_D7;
    case AreaCode.WSP_D8: return WspDistrictAgencyId.PEN_D8;
    default: return undefined;
  }
};

export const getDefaultViewport = (jogId: JogId | string = ""): Viewport => {
  const createViewObj = (latitude: number, longitude: number, zoom: number) => {
    return { latitude, longitude, zoom }
  }
  
  // For actual JOGs
  const ClarkCounty = createViewObj(45.69839378104345, -122.66282168292611, 10.40);
  const JBLM = createViewObj(46.99493139215082, -122.68381641682382, 10.22);
  const KingCountyNortheast = createViewObj(47.59588841857993, -122.08047019989192, 10.44);
  const KingCountySouth = createViewObj(47.394519133295375, -122.14410124420516, 10.66);
  const KitsapPeninsula = createViewObj(47.648751283542055, -123.50677640548192, 8.67);
  const LowerColumbiaRiver = createViewObj(46.244550851642316, -123.49303135573481, 9.30);
  const NorthwestWa = createViewObj(48.56852143682946, -122.43116200833029, 9.12);
  const Seattle = createViewObj(47.6062, -122.3321, 10);
  const SnohomishCounty = createViewObj(47.97032804081408, -121.95253938794517, 9.74);
  const Tacoma = createViewObj(47.17919387103203, -122.39243960781482, 10.78);

   // For "Groups" that are not actual JOGs
  const Ephrata = createViewObj(47.31726834490044, -119.55280036861137, 6.5); // For Eastern WA Group
  const Wenatchee = createViewObj(47.42341645562598, -120.31044328252437, 6); // For WSP "group"

  // For possible future JOGs
  const LewisCounty = createViewObj(46.66453203112545, -122.76350372954892, 9.74);
  const Spokane = createViewObj(47.6579413324723, -117.42349656531653, 9);
  const TriCities = createViewObj(46.22279349384027, -119.06992285209593, 9);

  
  switch (jogId as JogId) {
    case JogId.EasternWashington:
      return Ephrata;
    case JogId.KingCountyNortheast:
      return KingCountyNortheast;
    case JogId.KingCountySouth:
      return KingCountySouth;
    case JogId.Tacoma:
      return Tacoma;
    case JogId.JBLM:
      return JBLM;
    case JogId.SnohomishCounty:
      return SnohomishCounty;
    case JogId.KitsapPeninsula:
      return KitsapPeninsula;
    case JogId.ClarkCounty:
      return ClarkCounty;
    case JogId.LewisCounty:
      return LewisCounty;
    case JogId.LowerColumbiaRiver:
      return LowerColumbiaRiver;
    case JogId.NorthwestWashington:
      return NorthwestWa;
    case JogId.Spokane:
      return Spokane;
    case JogId.TriCities:
      return TriCities;
    case JogId.Wsp:
      return Wenatchee;
    case JogId.SeattleArea:
    default:
      return Seattle;
  }
};

export const sanitizeJogIds = (jogIds: string): string => {
  let jogIdSet = new Set(jogIds.split(","));
  if (jogIdSet.has(JogId.Wsp)) {
    // Remove the "WSP" JogId that's not a real JogId, so it's not sent in the API request
    jogIdSet.delete(JogId.Wsp);
    // Add all JogIds needed to retrieve WSP events for all WSP districts. (the Set will avoid duplicating any IDs)
    //JogIdsToRetrieveAllWspEvents.forEach(value => jogIdSet.add(value));
    AllJogIds.forEach(value => jogIdSet.add(value));
    return [...jogIdSet.values()].join(",");
  }  else {
    return jogIds;
  }  
}